import { useMemo, useState } from 'react';
import { FileTextOutlined, SearchOutlined, SettingOutlined } from '@ant-design/icons';
import { ScanLine } from 'lucide-react';

import { Bento, PageHeader, Tile } from '../../../components/shared';
import { Button, Empty, Input, Skeleton } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { usePermission } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { NHAN_CHE_DO } from '../../cau-hinh-ocr/constants';
import { useDanhSachLoaiChungTu } from '../../cau-hinh-ocr/hooks/useLoaiChungTu';
import { TypeBadge } from '../components/badges';

function khongDau(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/gi, 'd')
    .toLowerCase();
}

/**
 * Màn tìm-và-chọn loại chứng từ để nhận dạng (`/xu-ly/nhan-dang`). Loại chứng từ **cố ý không** là
 * mục con của thanh điều hướng: có thể có hàng chục loại, nên màn này mới là nơi tìm và chọn. Chỉ
 * hiện loại đang dùng — loại đã tắt không nhận dạng được.
 */
export function ChonKhuonView() {
  const navigate = useDieuHuong();
  const { t } = useT();
  const dichLoi = useLoi();
  const q = useDanhSachLoaiChungTu();
  const coCauHinh = usePermission('OCR_CONFIG');
  const [tuKhoa, setTuKhoa] = useState('');

  const dangDung = useMemo(() => (q.data ?? []).filter((s) => s.is_active), [q.data]);
  const dong = useMemo(() => {
    const tim = khongDau(tuKhoa.trim());
    if (!tim) return dangDung;
    return dangDung.filter((s) => khongDau(s.name).includes(tim) || khongDau(s.code).includes(tim));
  }, [dangDung, tuKhoa]);
  const coLoc = tuKhoa.trim() !== '';

  const noiDung = (() => {
    if (q.isLoading) {
      return (
        <Bento>
          {[0, 1, 2].map((i) => (
            <Tile key={i} span="sm:col-span-6 lg:col-span-4">
              <div className="space-y-3 px-5 py-4">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-3 w-1/3" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </Tile>
          ))}
        </Bento>
      );
    }
    if (q.error && !q.data) {
      return (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-default bg-surface px-4 py-3 shadow-sm">
          <span className="min-w-0 text-sm text-danger-700">{t('nhanDang.loiKhuon', { loi: dichLoi(q.error) })}</span>
          <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
            {t('chung.thuLai')}
          </Button>
        </div>
      );
    }
    if (dong.length === 0) {
      return (
        <div className="rounded-lg border border-default bg-surface shadow-sm">
          {coLoc ? (
            <Empty icon={FileTextOutlined} title={t('nhanDang.khongCoKhuonKhop')} />
          ) : (
            <Empty
              icon={FileTextOutlined}
              title={t('nhanDang.chuaCoKhuon')}
              description={t('nhanDang.chuaCoKhuonMo')}
              action={
                coCauHinh ? (
                  <Button variant="primary" size="md" icon={<SettingOutlined />} onClick={() => navigate('/xu-ly/cau-hinh-ocr')}>
                    {t('nhanDang.moThietLap')}
                  </Button>
                ) : undefined
              }
            />
          )}
        </div>
      );
    }
    return (
      <Bento>
        {dong.map((schema) => (
          <Tile key={schema.id} span="sm:col-span-6 lg:col-span-4" className="transition-shadow hover:shadow-md">
            <button
              type="button"
              onClick={() => navigate(`/xu-ly/nhan-dang/${encodeURIComponent(schema.code)}`)}
              className="h-full w-full rounded-lg px-5 py-4 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="truncate text-sm font-semibold text-content-primary" title={schema.name}>
                    {schema.name}
                  </h2>
                  <p className="mt-0.5 truncate font-mono text-xs text-content-muted">{schema.code}</p>
                </div>
                <TypeBadge type={schema.type} />
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-content-secondary">
                <span className="tabular-nums">{t('nhanDang.soTruong', { so: schema.field_count })}</span>
                <span className="tabular-nums">{t('nhanDang.soBang', { so: schema.table_count })}</span>
                <span className="text-content-muted">{t(NHAN_CHE_DO[schema.extraction_mode])}</span>
              </div>
            </button>
          </Tile>
        ))}
      </Bento>
    );
  })();

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[{ label: t('nav.nhom.xuLyTaiLieu') }, { label: t('nhanDang.chonKhuonTieuDe') }]}
          titleIcon={<ScanLine size={18} className="text-primary-600" />}
          title={t('nhanDang.chonKhuonTieuDe')}
          subtitle={t('nhanDang.chonKhuonMoTa')}
        />

        <Input
          className="w-full sm:max-w-sm"
          allowClear
          prefix={<SearchOutlined className="text-content-muted" />}
          placeholder={t('nhanDang.timKhuon')}
          aria-label={t('nhanDang.timKhuonNhan')}
          value={tuKhoa}
          onChange={(e) => setTuKhoa(e.target.value)}
        />

        {noiDung}
      </div>
    </div>
  );
}
